import { useEffect, useId, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { GUIDES } from '../../lib/hubCopy'
import { useHubLang } from '../../lib/i18n'
import { useSurfaceLayer } from '../../lib/useDismiss'
import { guideAskContext, guideHref, type GuideSheetState } from '../../lib/guides'
import { Dialog } from '../Dialog'
import { GuideBody } from './GuideBody'

/**
 * THE GUIDE SHEET (HLP-49, David 1 Nov) - one section of a guide, beside the
 * work it describes.
 *
 * David asked whether guides should be modals. The answer was split: a WHOLE
 * guide stays a page (long, sectioned, deep-linked, cited by Ask One - and a
 * modal on a phone is a page without a working Back button). The "How … works"
 * link on a working page opens the one section it names HERE, so the person
 * reads it with the page still in front of them.
 *
 *   - From the width at which Ask One docks (1280px; the stylesheet answers,
 *     see AppShell's `sheetDocked`) it takes Ask One's right-hand slot and is
 *     NON-MODAL: the page stays usable while reading, which is the point.
 *     The shell guarantees the sheet and Ask One are never both open.
 *   - Below that it is a bottom sheet through <Dialog variant="sheet"> - the
 *     trap, Escape, focus return and scroll lock that component learned the
 *     hard way are reused, not reimplemented - and it can be dragged down.
 *
 * It renders through GuideBody inside `.guide`, so the copy registry, the
 * widgets and the reading column's container rules apply as on the guides
 * page. A guide the person may not read (guides() did not return it) opens
 * NOTHING and says so in one line - never an error.
 *
 * `GuideSheetContent` is pure (no fetch, no effect) so the suite renders it.
 */
export function GuideSheet({
  state,
  docked,
  onClose,
  onAskOne,
}: {
  state: GuideSheetState
  docked: boolean
  onClose: () => void
  onAskOne: (context: ReturnType<typeof guideAskContext>) => void
}) {
  const t = GUIDES[useHubLang()]
  if (state.kind === 'refused' || state.kind === 'failed') {
    return (
      <p className="guidesheet__refused" role="status">
        <span>{state.kind === 'failed' ? t.loadFailed : t.sheetRefused}</span>
        <button type="button" className="dialog__x" aria-label={t.sheetClose} onClick={onClose}>
          ×
        </button>
      </p>
    )
  }
  const title = state.kind === 'section' ? state.section.title : t.title
  const body =
    state.kind === 'section' ? (
      <GuideSheetContent state={state} onAskOne={onAskOne} />
    ) : (
      <p className="empty" aria-busy="true">{t.sheetLoading}</p>
    )
  if (docked) {
    return (
      <DockedSheet title={title} onClose={onClose}>
        {body}
      </DockedSheet>
    )
  }
  return (
    <Dialog open title={title} onClose={onClose} variant="sheet" className="guidesheet__panel">
      <DragToClose onClose={onClose} />
      {body}
    </Dialog>
  )
}

export function GuideSheetContent({
  state,
  onAskOne,
}: {
  state: Extract<GuideSheetState, { kind: 'section' }>
  onAskOne: (context: ReturnType<typeof guideAskContext>) => void
}) {
  const t = GUIDES[useHubLang()]
  const { guide: g, section: s } = state
  const href = guideHref(g.key, s.key)
  const [copied, setCopied] = useState(false)
  return (
    <div className="guidesheet__content">
      <p className="guides__crumbs">
        <a href={href}>{g.title}</a>
      </p>
      <div className="guide">
        <section className="guide__section" lang={s.locale}>
          <GuideBody body={s.body} locale={s.locale} />
        </section>
      </div>
      <div className="guidesheet__acts">
        <a className="btn btn--sm" href={href}>
          {t.sheetOpenFull}
        </a>
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          onClick={() => {
            const url = `${location.origin}${location.pathname}${href}`
            void navigator.clipboard?.writeText(url).then(
              () => setCopied(true),
              () => undefined,
            )
          }}
        >
          {copied ? t.sheetCopied : t.sheetCopyLink}
        </button>
        <button type="button" className="btn btn--ai btn--sm" onClick={() => onAskOne(guideAskContext(g, s.key))}>
          {t.sheetAsk}
        </button>
      </div>
    </div>
  )
}

/**
 * Docked: beside the page, not over it. Focus goes in on open and back to the
 * link on close; Escape closes it when it is the top surface (useDismiss's
 * stack), and a press on the page does NOT - the page is meant to be used.
 */
function DockedSheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  const t = GUIDES[useHubLang()]
  const ref = useRef<HTMLElement>(null)
  const titleId = useId()
  const isTopmost = useSurfaceLayer(true)
  const latestClose = useRef(onClose)
  latestClose.current = onClose

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null
    ref.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape' || !isTopmost()) return
      if (document.querySelector('[aria-modal="true"]')) return
      latestClose.current()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (returnTo?.isConnected) returnTo.focus()
    }
  }, [isTopmost])

  return (
    <aside ref={ref} className="guidesheet" aria-labelledby={titleId} tabIndex={-1}>
      <header className="guidesheet__head">
        <h2 className="guidesheet__title" id={titleId}>{title}</h2>
        <button type="button" className="dialog__x" aria-label={`${t.sheetClose}: ${title}`} onClick={onClose}>
          ×
        </button>
      </header>
      <div className="guidesheet__body">{children}</div>
    </aside>
  )
}

/** A grab bar at the top of the bottom sheet: drag it down past a threshold
 *  to close. Pointer events only, no library; the × and Escape remain. */
function DragToClose({ onClose }: { onClose: () => void }) {
  const start = useRef<number | null>(null)
  const panel = (e: ReactPointerEvent<HTMLElement>) => e.currentTarget.closest('.dialog__panel') as HTMLElement | null
  return (
    <div
      className="guidesheet__grab"
      aria-hidden="true"
      onPointerDown={(e) => {
        start.current = e.clientY
        e.currentTarget.setPointerCapture(e.pointerId)
      }}
      onPointerMove={(e) => {
        if (start.current === null) return
        const p = panel(e)
        const dy = Math.max(0, e.clientY - start.current)
        if (p) p.style.transform = `translateY(${dy}px)`
      }}
      onPointerUp={(e) => {
        if (start.current === null) return
        const dy = e.clientY - start.current
        start.current = null
        const p = panel(e)
        if (p) p.style.transform = ''
        if (dy > 96) onClose()
      }}
      onPointerCancel={(e) => {
        start.current = null
        const p = panel(e)
        if (p) p.style.transform = ''
      }}
    />
  )
}
